import { useState, useEffect, useRef } from "react";
import { Calendar, Seo } from "../../components";
import { Clock, CalendarPlus, Download } from "lucide-react";
import { downloadIcs, openGoogleCalendar } from "../../features/icsGenerator";
import axios from "axios";
import { TRAININGS_PERIOD_URL } from "../../features/UrlHelper";
import { handleApiError } from "../../features/errorHandler";
import { formatDateForBackend } from "../../features/dateFormaters";
import "./RozvrhPage.scss";

const DAYS_SK = [
  "Nedeľa",
  "Pondelok",
  "Utorok",
  "Streda",
  "Štvrtok",
  "Piatok",
  "Sobota",
];
const DAYS_SHORT = ["Ne", "Po", "Ut", "St", "Št", "Pi", "So"];

function getWeekRange() {
  const today = new Date();
  const dow = (today.getDay() + 6) % 7; // 0 = pondelok
  const monday = new Date(today);
  monday.setDate(today.getDate() - dow);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { from: monday, to: sunday };
}

function getMonthRange(date) {
  return {
    from: new Date(date.getFullYear(), date.getMonth(), 1),
    to: new Date(date.getFullYear(), date.getMonth() + 1, 0),
  };
}

async function fetchPeriodEvents(from, to) {
  const response = await axios.post(TRAININGS_PERIOD_URL, {
    date_from: formatDateForBackend(from),
    date_to: formatDateForBackend(to),
  });
  return response.data;
}

function mapToCalendarEvents(data) {
  return data
    .filter((e) => e.title)
    .map((e) => ({
      date: e.date,
      title: e.title,
      description: e.description ?? "",
      timeFrom: e.time_from,
      timeTo: e.time_to,
      color: e.color,
    }));
}

function RozvrhPage() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [weekEvents, setWeekEvents] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [openMenu, setOpenMenu] = useState(null);
  const menuRef = useRef(null);

  const today = formatDateForBackend(new Date());

  // Close calendar menu on outside click
  useEffect(() => {
    if (!openMenu) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [openMenu]);

  useEffect(() => {
    const { from, to } = getWeekRange();
    fetchPeriodEvents(from, to)
      .then((data) => {
        const byDate = {};
        data.forEach((ev) => {
          if (!byDate[ev.date]) byDate[ev.date] = { date: ev.date, events: [] };
          if (ev.title) byDate[ev.date].events.push(ev);
        });
        setWeekEvents(
          Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)),
        );
      })
      .catch((err) => handleApiError(err, "Nepodarilo sa načítať týždenný rozvrh."));

    const { from: mFrom, to: mTo } = getMonthRange(new Date());
    fetchPeriodEvents(mFrom, mTo)
      .then((data) => setCalendarEvents(mapToCalendarEvents(data)))
      .catch((err) => handleApiError(err, "Nepodarilo sa načítať kalendár tréningov."));
  }, []);

  return (
    <div className="rozvrh">
      <Seo
        title="Tréningový rozvrh"
        description="Rozvrh tréningov Capoeira Bratislava. Nájdite si čas, ktorý vám vyhovuje – tréningy prebieha jú v Bratislave pre všetky úrovne."
        canonical="/rozvrh"
      />
      <section className="rozvrh__hero">
        <div className="rozvrh__hero-container">
          <span className="rozvrh__hero-tag">Rozvrh</span>
          <h1 className="rozvrh__hero-title">
            Pravidelné tréningy v Bratislave
          </h1>
          <p className="rozvrh__hero-subtitle">
            Tréningy pre deti, mládež aj dospelých prebiehajú pravidelne počas
            týždňa v bezpečnom a podpornom prostredí.
          </p>
        </div>
      </section>

      <section className="rozvrh__schedule">
        <div className="rozvrh__container">
          <h2 className="rozvrh__section-title">Týždenný rozvrh</h2>
          <p className="rozvrh__section-subtitle">
            Pravidelné tréningy počas týždňa
          </p>

          <div className="rozvrh__days">
            {weekEvents?.slice(0, 4)?.map(({ date, events }, i) => {
              const dow = new Date(date + "T12:00:00").getDay();
              const isToday = date === today;
              return (
                <div
                  key={i}
                  className={`rozvrh__day-card ${isToday ? "rozvrh__day-card--today" : ""}`}
                >
                  <div className="rozvrh__day-header">
                    <span className="rozvrh__day-short">{DAYS_SHORT[dow]}</span>
                    <span className="rozvrh__day-name">{DAYS_SK[dow]}</span>
                    {isToday && (
                      <span className="rozvrh__today-badge">Dnes</span>
                    )}
                  </div>
                  {events.length > 0 ? (
                    <div className="rozvrh__slots">
                      {events.map((ev, j) => (
                        <div key={j} className="rozvrh__slot">
                          <div
                            className="rozvrh__slot-indicator"
                            style={
                              ev.color ? { backgroundColor: ev.color } : {}
                            }
                          />
                          <div className="rozvrh__slot-content">
                            <span className="rozvrh__slot-label">
                              {ev.title}
                            </span>
                            {(ev.time_from || ev.time_to) && (
                              <span className="rozvrh__slot-time">
                                <Clock size={12} />
                                {ev.time_from} – {ev.time_to}
                              </span>
                            )}
                            {ev.description && (
                              <span className="rozvrh__slot-desc">
                                {ev.description}
                              </span>
                            )}
                          </div>
                          {ev.time_from && ev.time_to && (
                            <div
                              className="rozvrh__cal-menu"
                              ref={openMenu === `${i}-${j}` ? menuRef : null}
                            >
                              <button
                                className="rozvrh__slot-ics"
                                title="Pridať do kalendára"
                                aria-label={`Pridať ${ev.title} do kalendára`}
                                onClick={() =>
                                  setOpenMenu(
                                    openMenu === `${i}-${j}`
                                      ? null
                                      : `${i}-${j}`,
                                  )
                                }
                              >
                                <CalendarPlus size={14} />
                              </button>
                              {openMenu === `${i}-${j}` && (
                                <div className="rozvrh__cal-dropdown">
                                  <button
                                    className="rozvrh__cal-dropdown-item"
                                    onClick={() => {
                                      openGoogleCalendar({
                                        date,
                                        title: ev.title,
                                        description: ev.description,
                                        timeFrom: ev.time_from,
                                        timeTo: ev.time_to,
                                        recurring: true,
                                      });
                                      setOpenMenu(null);
                                    }}
                                  >
                                    <img
                                      src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg"
                                      alt=""
                                      width={14}
                                      height={14}
                                    />
                                    Google Calendar
                                  </button>
                                  <button
                                    className="rozvrh__cal-dropdown-item"
                                    onClick={() => {
                                      downloadIcs({
                                        date,
                                        title: ev.title,
                                        description: ev.description,
                                        timeFrom: ev.time_from,
                                        timeTo: ev.time_to,
                                        recurring: true,
                                      });
                                      setOpenMenu(null);
                                    }}
                                  >
                                    <Download size={14} />
                                    Stiahnuť .ics
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rozvrh__rest">
                      <span className="rozvrh__rest-text">Voľný deň</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="rozvrh__calendar-section">
        <div className="rozvrh__container">
          <h2 className="rozvrh__section-title">Kalendár tréningov</h2>
          <p className="rozvrh__section-subtitle">
            Kliknutím na deň zobrazíte naplánované tréningy.
          </p>
          <Calendar
            events={calendarEvents}
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
            onNext={({ from, to }) =>
              fetchPeriodEvents(from, to)
                .then((data) => setCalendarEvents(mapToCalendarEvents(data)))
                .catch((err) => handleApiError(err, "Nepodarilo sa načítať kalendár tréningov."))
            }
            onPrevious={({ from, to }) =>
              fetchPeriodEvents(from, to)
                .then((data) => setCalendarEvents(mapToCalendarEvents(data)))
                .catch((err) => handleApiError(err, "Nepodarilo sa načítať kalendár tréningov."))
            }
          />
        </div>
      </section>
    </div>
  );
}

export default RozvrhPage;
